import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import tmdbService from '../services/tmdb';
import StorageService from '../utils/storage';
import LazyImage from './LazyImage';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    transform: translateY(100px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: ${fadeIn} 0.3s ease;
  backdrop-filter: blur(5px);
`;

const ModalContent = styled.div`
  position: relative;
  width: 90%;
  max-width: 1200px;
  max-height: 90vh;
  background: linear-gradient(135deg, #1a1033 0%, #2b1b54 50%, #392667 100%);
  border-radius: 8px;
  overflow-y: auto;
  animation: ${slideUp} 0.3s ease;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }
`;

const BackgroundImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 400px;
  background-image: url(${props => props.$bgImage});
  background-size: cover;
  background-position: center;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      to bottom,
      rgba(26, 16, 51, 0.8) 0%,
      rgba(26, 16, 51, 0.9) 50%,
      #1a1033 100%
    );
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(229, 9, 20, 0.9);
  border: none;
  color: white;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);

  &:hover {
    background: #F40D17;
    transform: scale(1.1);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.4);
  }

  svg {
    width: 24px;
    height: 24px;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 2;
  padding: 40px;
  color: white;
`;

const Header = styled.div`
  display: flex;
  gap: 30px;
  margin-bottom: 40px;
`;

const Poster = styled.div`
  width: 300px;
  height: 450px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Info = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const MainInfo = styled.div`
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  margin: 0 0 10px;
`;

const MetaInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const Badge = styled.span`
  background: ${props => props.$type === 'quality' ? props.$color : 'rgba(255, 255, 255, 0.1)'};
  color: white;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
`;

const Rating = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  color: #ffd700;
  font-weight: 500;
`;

const Overview = styled.p`
  font-size: 16px;
  line-height: 1.6;
  margin: 20px 0;
  color: rgba(255, 255, 255, 0.9);
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 30px;
`;

const Button = styled.button`
  background: ${props => props.$primary ? '#E50914' : 'rgba(255, 255, 255, 0.1)'};
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    background: ${props => props.$primary ? '#F40D17' : 'rgba(255, 255, 255, 0.2)'};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Section = styled.div`
  margin-top: 40px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 20px;
`;

const SeasonSelect = styled.select`
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 10px 15px;
  border-radius: 4px;
  font-size: 16px;
  margin-bottom: 20px;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #E50914;
  }
  
  option {
    background: #2b1b54;
  }
`;

const EpisodesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 20px;
`;

const EpisodeCard = styled.div`
  display: flex;
  gap: 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 15px;
  cursor: ${props => props.$available ? 'pointer' : 'default'};
  transition: all 0.2s ease;
  opacity: ${props => props.$available ? 1 : 0.6};
  
  &:hover {
    background: ${props => props.$available ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)'};
    transform: ${props => props.$available ? 'translateY(-2px)' : 'none'};
  }
`;

const EpisodeThumbnail = styled.div`
  width: 180px;
  height: 100px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const EpisodeInfo = styled.div`
  flex: 1;
`;

const EpisodeTitle = styled.h3`
  font-size: 18px;
  font-weight: 500;
  margin: 0 0 5px;
`;

const EpisodeNumber = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 10px;
`;

const EpisodeOverview = styled.p`
  font-size: 14px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.8);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

// Componentes de Skeleton para loading
const SkeletonPulse = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const Skeleton = styled.div`
  background: linear-gradient(90deg, 
    rgba(255, 255, 255, 0.1) 25%, 
    rgba(255, 255, 255, 0.2) 37%, 
    rgba(255, 255, 255, 0.1) 63%
  );
  background-size: 400% 100%;
  animation: ${SkeletonPulse} 1.4s ease infinite;
  border-radius: 4px;
`;

const PosterSkeleton = styled(Skeleton)`
  width: 300px;
  height: 450px;
`;

const TitleSkeleton = styled(Skeleton)`
  width: 60%;
  height: 40px;
  margin-bottom: 20px;
`;

const TextSkeleton = styled(Skeleton)`
  width: ${props => props.$width || '100%'};
  height: ${props => props.$height || '20px'};
  margin-bottom: 10px;
`;

const EpisodeSkeleton = styled(Skeleton)`
  width: 100%;
  height: 130px;
  margin-bottom: 15px;
`;

// Adicionar um novo componente para o ícone de verificação
const WatchedBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4caf50;
  z-index: 2;
`;

// Adicionar um botão para marcar como assistido
const WatchedButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.$watched ? '#4caf50' : 'rgba(255, 255, 255, 0.6)'};
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 0;
  cursor: pointer;
  margin-top: 5px;
  transition: all 0.2s ease;
  
  &:hover {
    color: ${props => props.$watched ? '#66bb6a' : 'rgba(255, 255, 255, 0.8)'};
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

// Adicionar componentes para a barra de progresso
const ProgressContainer = styled.div`
  margin-top: 15px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ProgressInfo = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${props => props.$progress || 0}%;
    background: #E50914;
    border-radius: 3px;
    transition: width 0.3s ease;
  }
`;

// Adicionar componente para os botões de ação da temporada
const SeasonActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 15px;
`;

const SeasonActionButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const SeriesDetailsModal = ({ 
  isOpen, 
  onClose, 
  seriesData,
  onWatchEpisode,
  isFavorite,
  onToggleFavorite 
}) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(1);
  const [seasonDetails, setSeasonDetails] = useState(null);
  const [seasonLoading, setSeasonLoading] = useState(false);
  const [watchedEpisodes, setWatchedEpisodes] = useState({});

  // Carregar episódios assistidos do localStorage
  useEffect(() => {
    if (seriesData && seriesData.title) {
      const storageKey = `watched_episodes_${seriesData.title}`;
      const savedWatched = localStorage.getItem(storageKey);
      if (savedWatched) {
        setWatchedEpisodes(JSON.parse(savedWatched));
      } else {
        setWatchedEpisodes({});
      }
    }
  }, [seriesData]);

  // Função para marcar/desmarcar episódio como assistido
  const toggleWatched = (seasonNumber, episodeNumber) => {
    if (!seriesData || !seriesData.title) return;
    
    const episodeKey = `S${seasonNumber}E${episodeNumber}`;
    const storageKey = `watched_episodes_${seriesData.title}`;
    
    setWatchedEpisodes(prev => {
      const updated = { ...prev };
      
      if (updated[episodeKey]) {
        delete updated[episodeKey];
      } else {
        updated[episodeKey] = true;
      }
      
      // Salvar no localStorage
      localStorage.setItem(storageKey, JSON.stringify(updated));
      
      return updated;
    });
  };

  // Verificar se um episódio está marcado como assistido
  const isEpisodeWatched = (seasonNumber, episodeNumber) => {
    const episodeKey = `S${seasonNumber}E${episodeNumber}`;
    return !!watchedEpisodes[episodeKey];
  };

  useEffect(() => {
    const fetchDetails = async () => {
      if (!seriesData) return;

      try {
        setLoading(true);
        setError(null);

        console.log('Buscando detalhes para série:', seriesData);

        // Verificar se já temos os dados do TMDB pré-carregados
        if (seriesData.tmdbData) {
          console.log('Usando dados TMDB pré-carregados');
          setDetails(seriesData.tmdbData);
          setLoading(false);
          return;
        }

        // Verificar cache
        const cacheKey = `tmdb_series_${seriesData.title}`;
        const cachedData = localStorage.getItem(cacheKey);
        
        if (cachedData) {
          console.log('Usando dados em cache para série');
          setDetails(JSON.parse(cachedData));
          setLoading(false);
          return;
        }

        const metadata = await tmdbService.getMetadata(seriesData.title, 'tv');

        if (metadata) {
          console.log('Dados TMDB recebidos:', metadata);
          // Salvar no cache
          localStorage.setItem(cacheKey, JSON.stringify(metadata));
          setDetails(metadata);
        } else {
          setError('Dados não encontrados no TMDB');
        }
      } catch (err) {
        console.error('Erro ao buscar detalhes:', err);
        setError('Erro ao carregar informações do TMDB');
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && seriesData) {
      fetchDetails();
    }
  }, [isOpen, seriesData]);

  useEffect(() => {
    const fetchSeasonDetails = async () => {
      if (!details || !selectedSeason) return;

      try {
        setSeasonLoading(true);

        // Verificar cache
        const cacheKey = `tmdb_season_${details.id}_${selectedSeason}`;
        const cachedData = localStorage.getItem(cacheKey);
        
        if (cachedData) {
          setSeasonDetails(JSON.parse(cachedData));
          setSeasonLoading(false);
          return;
        }

        const seasonData = await tmdbService.getSeasonDetails(details.id, selectedSeason);

        if (seasonData) {
          // Salvar no cache
          localStorage.setItem(cacheKey, JSON.stringify(seasonData));
          setSeasonDetails(seasonData);
        }
      } catch (err) {
        console.error('Erro ao buscar detalhes da temporada:', err);
      } finally {
        setSeasonLoading(false);
      }
    };

    if (details) {
      fetchSeasonDetails();
    }
  }, [details, selectedSeason]);

  const handleSeasonChange = (e) => {
    setSelectedSeason(parseInt(e.target.value));
  };

  const findEpisodeInSeriesData = (seasonNumber, episodeNumber) => {
    if (!seriesData || !seriesData.seasons) {
      console.log('Dados de série não disponíveis ou sem temporadas');
      return null;
    }
    
    console.log(`Procurando S${seasonNumber}E${episodeNumber} em:`, seriesData.seasons);
    
    // Verificar se a temporada existe
    const season = seriesData.seasons[seasonNumber];
    if (!season) {
      // Tentar encontrar o episódio em outras temporadas (às vezes a numeração pode estar diferente)
      console.log(`Temporada ${seasonNumber} não encontrada, procurando em outras temporadas`);
      
      for (const [otherSeasonNum, episodes] of Object.entries(seriesData.seasons)) {
        const foundEpisode = episodes.find(ep => ep.episode === episodeNumber);
        if (foundEpisode) {
          console.log(`Episódio encontrado na temporada ${otherSeasonNum}:`, foundEpisode);
          return foundEpisode;
        }
      }
      
      console.log(`Episódio não encontrado em nenhuma temporada`);
      return null;
    }
    
    // Procurar o episódio na temporada correta
    const episode = season.find(ep => ep.episode === episodeNumber);
    
    // Se não encontrar pelo número exato, tentar encontrar um episódio próximo
    if (!episode && season.length > 0) {
      // Ordenar episódios por número
      const sortedEpisodes = [...season].sort((a, b) => a.episode - b.episode);
      
      // Encontrar o episódio mais próximo
      let closestEpisode = null;
      let minDiff = Number.MAX_SAFE_INTEGER;
      
      for (const ep of sortedEpisodes) {
        const diff = Math.abs(ep.episode - episodeNumber);
        if (diff < minDiff) {
          minDiff = diff;
          closestEpisode = ep;
        }
      }
      
      // Se a diferença for pequena (até 2 episódios), considerar como correspondente
      if (minDiff <= 2) {
        console.log(`Episódio exato não encontrado, usando o mais próximo (${closestEpisode.episode}):`, closestEpisode);
        return closestEpisode;
      }
    }
    
    console.log(`Episódio encontrado:`, episode);
    return episode;
  };

  // Calcular o progresso da série
  const calculateProgress = () => {
    if (!seasonDetails || !seasonDetails.episodes || !seriesData || !seriesData.seasons) {
      return { watched: 0, total: 0, percent: 0 };
    }
    
    // Contar episódios disponíveis na temporada atual
    const availableEpisodes = seasonDetails.episodes
      .filter(episode => findEpisodeInSeriesData(selectedSeason, episode.episode_number))
      .length;
    
    // Contar episódios assistidos na temporada atual
    const watchedCount = seasonDetails.episodes
      .filter(episode => {
        const episodeKey = `S${selectedSeason}E${episode.episode_number}`;
        return watchedEpisodes[episodeKey];
      })
      .length;
    
    const percent = availableEpisodes > 0 ? (watchedCount / availableEpisodes) * 100 : 0;
    
    return {
      watched: watchedCount,
      total: availableEpisodes,
      percent
    };
  };

  const progress = calculateProgress();

  // Função para marcar todos os episódios da temporada como assistidos
  const markAllAsWatched = () => {
    if (!seriesData || !seriesData.title || !seasonDetails || !seasonDetails.episodes) return;
    
    const storageKey = `watched_episodes_${seriesData.title}`;
    const updatedWatched = { ...watchedEpisodes };
    
    // Marcar apenas episódios disponíveis
    seasonDetails.episodes.forEach(episode => {
      const episodeData = findEpisodeInSeriesData(selectedSeason, episode.episode_number);
      if (episodeData) {
        const episodeKey = `S${selectedSeason}E${episode.episode_number}`;
        updatedWatched[episodeKey] = true;
      }
    });
    
    // Salvar no localStorage
    localStorage.setItem(storageKey, JSON.stringify(updatedWatched));
    setWatchedEpisodes(updatedWatched);
  };
  
  // Função para marcar todos os episódios da temporada como não assistidos
  const markAllAsUnwatched = () => {
    if (!seriesData || !seriesData.title || !seasonDetails || !seasonDetails.episodes) return;
    
    const storageKey = `watched_episodes_${seriesData.title}`;
    const updatedWatched = { ...watchedEpisodes };
    
    // Desmarcar episódios da temporada atual
    seasonDetails.episodes.forEach(episode => {
      const episodeKey = `S${selectedSeason}E${episode.episode_number}`;
      if (updatedWatched[episodeKey]) {
        delete updatedWatched[episodeKey];
      }
    });
    
    // Salvar no localStorage
    localStorage.setItem(storageKey, JSON.stringify(updatedWatched));
    setWatchedEpisodes(updatedWatched);
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalContent>
        <CloseButton onClick={onClose}>
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </CloseButton>

        {loading ? (
          <ContentWrapper>
            <Header>
              <PosterSkeleton />
              <Info>
                <TitleSkeleton />
                <TextSkeleton $width="30%" />
                <TextSkeleton $width="40%" />
                <TextSkeleton $width="80%" $height="100px" />
              </Info>
            </Header>
            <Section>
              <SectionTitle>Episódios</SectionTitle>
              <TextSkeleton $width="200px" $height="40px" />
              {[1, 2, 3, 4].map(i => (
                <EpisodeSkeleton key={i} />
              ))}
            </Section>
          </ContentWrapper>
        ) : error ? (
          <ContentWrapper>
            <Header>
              <Poster>
                <LazyImage 
                  src={seriesData.logo || '/placeholder.png'} 
                  alt={seriesData.title}
                />
              </Poster>
              <Info>
                <Title>{seriesData.title}</Title>
                <MetaInfo>
                  <Badge>{Object.keys(seriesData.seasons).length} Temporada(s)</Badge>
                </MetaInfo>
                <Overview>{error}</Overview>
                <ActionButtons>
                  <Button onClick={onToggleFavorite}>
                    {isFavorite ? (
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z" fill="#FFD700"/>
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                    {isFavorite ? 'Remover dos Favoritos' : 'Adicionar aos Favoritos'}
                  </Button>
                </ActionButtons>
              </Info>
            </Header>
          </ContentWrapper>
        ) : details ? (
          <>
            <BackgroundImage $bgImage={tmdbService.getImageUrl(details.backdrop_path)} />
            <ContentWrapper>
              <Header>
                <Poster>
                  <LazyImage 
                    src={tmdbService.getImageUrl(details.poster_path) || seriesData.logo || '/placeholder.png'} 
                    alt={details.name}
                  />
                </Poster>
                <Info>
                  <MainInfo>
                    <Title>{details.name}</Title>
                    <MetaInfo>
                      {details.genres?.map(genre => (
                        <Badge key={genre.id}>{genre.name}</Badge>
                      ))}
                      {details.first_air_date && (
                        <Badge>
                          {new Date(details.first_air_date).getFullYear()}
                        </Badge>
                      )}
                      {details.number_of_seasons && (
                        <Badge>
                          {details.number_of_seasons} Temporada(s)
                        </Badge>
                      )}
                      {details.vote_average && (
                        <Rating>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z"/>
                          </svg>
                          {details.vote_average.toFixed(1)}
                        </Rating>
                      )}
                    </MetaInfo>
                    <Overview>{details.overview}</Overview>
                    <ActionButtons>
                      <Button onClick={onToggleFavorite}>
                        {isFavorite ? (
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z" fill="#FFD700"/>
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                        {isFavorite ? 'Remover dos Favoritos' : 'Adicionar aos Favoritos'}
                      </Button>
                    </ActionButtons>
                  </MainInfo>

                  <Section>
                    <SectionTitle>Episódios</SectionTitle>
                    <SeasonSelect value={selectedSeason} onChange={handleSeasonChange}>
                      {details.seasons?.filter(season => season.season_number > 0).map(season => (
                        <option key={season.id} value={season.season_number}>
                          Temporada {season.season_number}
                        </option>
                      ))}
                    </SeasonSelect>
                    
                    {!seasonLoading && (
                      <ProgressContainer>
                        <ProgressInfo>
                          <span>Progresso da temporada</span>
                          <span>{progress.watched} de {progress.total} episódios assistidos</span>
                        </ProgressInfo>
                        <ProgressBar $progress={progress.percent} />
                        
                        <SeasonActions>
                          <SeasonActionButton onClick={markAllAsWatched}>
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor" />
                            </svg>
                            Marcar todos como assistidos
                          </SeasonActionButton>
                          <SeasonActionButton onClick={markAllAsUnwatched}>
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z" fill="currentColor" />
                            </svg>
                            Marcar todos como não assistidos
                          </SeasonActionButton>
                        </SeasonActions>
                      </ProgressContainer>
                    )}
                    
                    {seasonLoading ? (
                      <>
                        {[1, 2, 3].map(i => (
                          <EpisodeSkeleton key={i} />
                        ))}
                      </>
                    ) : (
                      <EpisodesList>
                        {seasonDetails?.episodes?.map(episode => {
                          const episodeData = findEpisodeInSeriesData(selectedSeason, episode.episode_number);
                          const isAvailable = !!episodeData;
                          const watched = isEpisodeWatched(selectedSeason, episode.episode_number);
                          
                          return (
                            <EpisodeCard 
                              key={episode.id} 
                              onClick={() => isAvailable && onWatchEpisode(episodeData)}
                              $available={isAvailable}
                              style={{ opacity: watched ? 0.7 : (isAvailable ? 1 : 0.6) }}
                            >
                              <EpisodeThumbnail>
                                {watched && (
                                  <WatchedBadge>
                                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor" />
                                    </svg>
                                  </WatchedBadge>
                                )}
                                <LazyImage 
                                  src={tmdbService.getImageUrl(episode.still_path, 'w300') || '/placeholder-episode.png'} 
                                  alt={episode.name}
                                />
                              </EpisodeThumbnail>
                              <EpisodeInfo>
                                <EpisodeTitle>{episode.name}</EpisodeTitle>
                                <EpisodeNumber>
                                  Episódio {episode.episode_number} • {isAvailable ? (
                                    <span style={{ color: '#4caf50' }}>Disponível</span>
                                  ) : (
                                    <span style={{ color: '#f44336' }}>Não disponível</span>
                                  )}
                                </EpisodeNumber>
                                <EpisodeOverview>{episode.overview || 'Sem descrição disponível.'}</EpisodeOverview>
                                
                                {isAvailable && (
                                  <WatchedButton 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleWatched(selectedSeason, episode.episode_number);
                                    }}
                                    $watched={watched}
                                  >
                                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      {watched ? (
                                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor" />
                                      ) : (
                                        <path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z" fill="currentColor" />
                                      )}
                                    </svg>
                                    {watched ? 'Marcar como não assistido' : 'Marcar como assistido'}
                                  </WatchedButton>
                                )}
                              </EpisodeInfo>
                            </EpisodeCard>
                          );
                        })}
                      </EpisodesList>
                    )}
                  </Section>
                </Info>
              </Header>
            </ContentWrapper>
          </>
        ) : null}
      </ModalContent>
    </ModalOverlay>
  );
};

export default SeriesDetailsModal; 